import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useCompany } from '../../CompanyContext';
import { mensajeGuardarTrabajador } from '../../lib/unicidad';
import { etiquetaSede, matrizDe, muestraSelectorSede, opcionesSede, type FilaSede } from '../../lib/sedes';
import { logAudit } from '../../auditLog';
import { MENSAJE_TRABAJADOR_CON_FIRMAS, trabajadorTieneRegistrosFirmados } from '../../lib/firmas/firmas-trabajador';
import SelectBuscable from '../SelectBuscable';
import AtencionPrioritariaSeccion from './AtencionPrioritariaSeccion';
import LicenciasConduccionSeccion from '../licencias/LicenciasConduccionSeccion';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ContractType = 'indefinido' | 'plazo_fijo' | 'obra_cierta' | 'pasantia' | 'otro';
export type WorkerStatus = 'activo' | 'licencia' | 'desvinculado';
export type RiskLevel = 'bajo' | 'medio' | 'alto';
export type MedicalAptitude = 'apto' | 'apto_con_restricciones' | 'no_apto' | 'pendiente';
export type ExamType = 'pre_ocupacional' | 'periodico' | 'reintegro' | 'retiro';
export type PriorityGroupType = 'embarazada' | 'discapacidad' | 'menor_edad' | 'otro';

export interface Worker {
  id: string;
  company_id: string;
  full_name: string;
  cedula: string | null;
  birth_date: string | null;
  sex: 'masculino' | 'femenino' | 'otro' | null;
  position: string;
  position_id: string | null;
  warehouse_id: string | null;
  site_id?: string | null;   // M40: sede; si no se envía, la base pone la Matriz
  contract_type: ContractType | null;
  start_date: string | null;
  end_date: string | null;
  status: WorkerStatus;
  risk_level: RiskLevel | null;
  is_teleworker: boolean;
  is_priority_group: boolean;
  priority_group_type: PriorityGroupType | null;
  last_medical_exam: string | null;
  exam_type: ExamType | null;
  medical_aptitude: MedicalAptitude | null;
  physician: string | null;
  notes: string | null;
}

interface MedicalExam {
  id: string;
  exam_date: string;
  exam_type: ExamType;
  medical_aptitude: MedicalAptitude;
  physician: string | null;
  notes: string | null;
}

interface Props {
  companyId: string;
  warehouses: { id: string; name: string }[];
  jobPositions: { id: string; name: string }[];
  /** F1a: sedes de la empresa. El campo "Sede" aparece solo con 2 o más activas; con una, el payload no cambia. */
  sedes?: FilaSede[];
  worker?: Worker | null;
  onSaved: () => void;
  onClose: () => void;
}

// ── Validation ─────────────────────────────────────────────────────────────────

function validateCedula(cedula: string): boolean {
  if (!/^\d{10}$/.test(cedula)) return false;
  const province = parseInt(cedula.substring(0, 2));
  if (province < 1 || province > 24) return false;
  const coefficients = [2, 1, 2, 1, 2, 1, 2, 1, 2];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let val = parseInt(cedula[i]) * coefficients[i];
    if (val >= 10) val -= 9;
    sum += val;
  }
  return (10 - (sum % 10)) % 10 === parseInt(cedula[9]);
}

// ── Labels ─────────────────────────────────────────────────────────────────────

const CONTRACT_LABELS: Record<ContractType, string> = {
  indefinido:  'Indefinido',
  plazo_fijo:  'Plazo fijo',
  obra_cierta: 'Obra cierta',
  pasantia:    'Pasantía',
  otro:        'Otro',
};

const STATUS_LABELS: Record<WorkerStatus, string> = {
  activo:       'Activo',
  licencia:     'En licencia',
  desvinculado: 'Desvinculado',
};

export const APTITUDE_LABELS: Record<MedicalAptitude, string> = {
  apto:                 'Apto',
  apto_con_restricciones: 'Apto con restricciones',
  no_apto:              'No apto',
  pendiente:            'Pendiente',
};

export const EXAM_TYPE_LABELS: Record<ExamType, string> = {
  pre_ocupacional: 'Pre-ocupacional',
  periodico:       'Periódico',
  reintegro:       'Reingreso',
  retiro:          'Retiro',
};

const RISK_COLORS: Record<RiskLevel, { bg: string; color: string; border: string }> = {
  bajo:  { bg: 'rgba(34,197,94,0.1)',  color: '#22c55e', border: 'rgba(34,197,94,0.3)'  },
  medio: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
  alto:  { bg: 'rgba(239,68,68,0.1)',  color: '#ef4444', border: 'rgba(239,68,68,0.3)'  },
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function WorkerModal({ companyId, warehouses, jobPositions, sedes = [], worker, onSaved, onClose }: Props) {
  const isEdit = !!worker;
  const { profile } = useCompany();
  const canViewMedicalHistory = profile?.role === 'admin' || profile?.role === 'superadmin';

  // Datos personales
  const [fullName, setFullName]   = useState(worker?.full_name ?? '');
  const [cedula, setCedula]       = useState(worker?.cedula ?? '');
  const [birthDate, setBirthDate] = useState(worker?.birth_date ?? '');
  const [sex, setSex]             = useState<'masculino' | 'femenino' | 'otro' | ''>(worker?.sex ?? '');

  // Datos laborales
  const [positionId, setPositionId]     = useState(worker?.position_id ?? '');
  const [warehouseId, setWarehouseId]   = useState(worker?.warehouse_id ?? '');
  const conSede = muestraSelectorSede(sedes);
  const [siteId, setSiteId]             = useState(worker?.site_id ?? matrizDe(sedes)?.id ?? '');
  const [contractType, setContractType] = useState<ContractType | ''>(worker?.contract_type ?? '');
  const [startDate, setStartDate]       = useState(worker?.start_date ?? '');
  const [endDate, setEndDate]           = useState(worker?.end_date ?? '');
  const [status, setStatus]             = useState<WorkerStatus>(worker?.status ?? 'activo');

  // SSO
  const [riskLevel, setRiskLevel]               = useState<RiskLevel | ''>(worker?.risk_level ?? '');
  const [isTeleworker, setIsTeleworker]         = useState(worker?.is_teleworker ?? false);

  // Salud
  const [lastExamDate, setLastExamDate]     = useState(worker?.last_medical_exam ?? '');
  const [examType, setExamType]             = useState<ExamType | ''>(worker?.exam_type ?? '');
  const [aptitude, setAptitude]             = useState<MedicalAptitude | ''>(worker?.medical_aptitude ?? '');
  const [physician, setPhysician]           = useState(worker?.physician ?? '');
  const [notes, setNotes]                   = useState(worker?.notes ?? '');

  // UI state
  const [examHistory, setExamHistory] = useState<MedicalExam[]>([]);
  const [saving, setSaving]           = useState(false);
  const [errors, setErrors]           = useState<Record<string, string>>({});
  const [confirmDesvincular, setConfirmDesvincular] = useState(false);
  const [confirmDelete, setConfirmDelete]           = useState(false);

  useEffect(() => {
    if (!isEdit || !worker?.id) return;
    supabase
      .from('worker_medical_exams')
      .select('id, exam_date, exam_type, medical_aptitude:aptitude, physician, notes')
      .eq('worker_id', worker.id)
      .order('exam_date', { ascending: false })
      .then(({ data }) => setExamHistory((data ?? []) as MedicalExam[]));
  }, [isEdit, worker?.id]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!fullName.trim()) e.fullName = 'El nombre es requerido.';
    // F1a2: obligatorio también sin puestos creados (antes se saltaba la validación); la base admite null, el front no.
    if (!positionId) e.position = jobPositions.length === 0
      ? 'El puesto de trabajo es requerido: crea uno en Puestos de Trabajo.'
      : 'El puesto de trabajo es requerido.';
    if (cedula && !validateCedula(cedula)) e.cedula = 'Cédula inválida (módulo 10 Ecuador).';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // F1a2: Historial de Cambios ("Trabajadores"). Solo nombre, puesto, sede y la acción: nunca cédula ni datos médicos.
  function registrar(action: 'created' | 'updated' | 'deleted', id: string, extra: Record<string, unknown> = {}) {
    const sedeId = conSede ? siteId : worker?.site_id ?? matrizDe(sedes)?.id;
    logAudit({
      company_id: companyId, action, entity_type: 'worker', entity_id: id, entity_name: fullName.trim() || worker?.full_name || '',
      details: {
        puesto: jobPositions.find(p => p.id === positionId)?.name ?? worker?.position ?? null,
        sede: sedes.find(s => s.id === sedeId)?.name ?? null,
        ...extra,
      },
    });
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);

    const data = {
      company_id:          companyId,
      full_name:           fullName.trim(),
      cedula:              cedula.trim() || null,
      birth_date:          birthDate || null,
      sex:                 sex || null,
      position_id:         positionId || null,
      position:            jobPositions.find(p => p.id === positionId)?.name ?? '',
      warehouse_id:        warehouseId || null,
      contract_type:       contractType || null,
      start_date:          startDate || null,
      end_date:            contractType !== 'indefinido' ? endDate || null : null,
      status,
      risk_level:          riskLevel || null,
      is_teleworker:       isTeleworker,
      // Lote D (M49, TH 1): la condición de atención prioritaria ya no se escribe en la ficha (dato sensible): va en su
      // tabla aparte, solo para el administrador. Los valores anteriores de la ficha no se tocan.
      last_medical_exam:   lastExamDate || null,
      exam_type:           examType || null,
      medical_aptitude:    aptitude || null,
      physician:           physician.trim() || null,
      notes:               notes.trim() || null,
      // Solo con el selector visible (2 o más sedes); con una sede el payload es el de siempre y la base pone la Matriz.
      ...(conSede && siteId ? { site_id: siteId } : {}),
    };

    let workerId = worker?.id;

    if (isEdit && workerId) {
      const { error } = await supabase.from('workers').update(data).eq('id', workerId);
      if (error) {
        alert(mensajeGuardarTrabajador(error));
        setSaving(false);
        return;
      }
      registrar('updated', workerId);
      if (lastExamDate && lastExamDate !== worker?.last_medical_exam) {
        const { error: examError } = await supabase.from('worker_medical_exams').insert({
          company_id:  companyId,
          worker_id:   workerId,
          exam_date:   lastExamDate,
          exam_type:   examType || null,
          aptitude:    aptitude || null,
          physician:   physician.trim() || null,
          notes:       notes.trim() || null,
        });
        if (examError) {
          alert('Error al guardar examen: ' + examError.message);
          setSaving(false);
          return;
        }
      }
    } else {
      const { data: inserted, error } = await supabase
        .from('workers')
        .insert(data)
        .select('id')
        .single();
      if (error) {
        alert(mensajeGuardarTrabajador(error));
        setSaving(false);
        return;
      }
      workerId = inserted?.id;
      if (workerId) registrar('created', workerId);
      if (workerId && lastExamDate) {
        const { error: examError } = await supabase.from('worker_medical_exams').insert({
          company_id:  companyId,
          worker_id:   workerId,
          exam_date:   lastExamDate,
          exam_type:   examType || null,
          aptitude:    aptitude || null,
          physician:   physician.trim() || null,
          notes:       notes.trim() || null,
        });
        if (examError) {
          alert('Error al guardar examen: ' + examError.message);
          setSaving(false);
          return;
        }
      }
    }

    setSaving(false);
    onSaved();
  }

  async function handleDesvincular() {
    if (!worker?.id) return;
    setSaving(true);
    const { error } = await supabase.from('workers').update({ status: 'desvinculado' }).eq('id', worker.id);
    if (error) {
      alert('Error al desvincular trabajador: ' + error.message);
      setSaving(false);
      return;
    }
    registrar('updated', worker.id, { estado: 'desvinculado' });
    setSaving(false);
    onSaved();
  }

  async function handleDelete() {
    if (!worker?.id) return;
    setSaving(true);
    // Registros firmados (M45): se conservan como evidencia; se avisa antes de intentar el borrado.
    if (await trabajadorTieneRegistrosFirmados(worker.id)) {
      alert(MENSAJE_TRABAJADOR_CON_FIRMAS);
      setSaving(false);
      return;
    }
    const { error: examError } = await supabase.from('worker_medical_exams').delete().eq('worker_id', worker.id);
    if (examError) {
      alert('Error al eliminar exámenes: ' + examError.message);
      setSaving(false);
      return;
    }
    const { error } = await supabase.from('workers').delete().eq('id', worker.id);
    if (error) {
      alert('Error al eliminar: ' + error.message);
      setSaving(false);
      return;
    }
    registrar('deleted', worker.id);
    setSaving(false);
    onSaved();
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', zIndex: 400, padding: '24px 16px', overflowY: 'auto',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
        width: '100%', maxWidth: 580, boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        marginTop: 'auto', marginBottom: 'auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid #334155',
        }}>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
            {isEdit ? 'Editar trabajador' : 'Agregar trabajador'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Sección: Datos personales ─────────────────────────────── */}
          <Section title="Datos personales">
            <Field label="Nombre completo *" error={errors.fullName}>
              <input style={inputStyle} value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Ej: Ana García López" />
            </Field>
            <Row>
              <Field label="Cédula (10 dígitos)" error={errors.cedula}>
                <input style={inputStyle} value={cedula} maxLength={10}
                  onChange={e => setCedula(e.target.value.replace(/\D/g, ''))}
                  placeholder="0102030405" />
              </Field>
              <Field label="Fecha de nacimiento">
                <input style={inputStyle} type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />
              </Field>
            </Row>
            <Field label="Sexo">
              <select style={selectStyle} value={sex} onChange={e => setSex(e.target.value as 'masculino' | 'femenino' | 'otro' | '')}>
                <option value="">— Seleccionar —</option>
                <option value="masculino">Masculino</option>
                <option value="femenino">Femenino</option>
                <option value="otro">Otro</option>
              </select>
            </Field>
          </Section>

          {/* ── Sección: Datos laborales ──────────────────────────────── */}
          <Section title="Datos laborales">
            <Row>
              <Field label="Puesto de trabajo *" error={errors.position}>
                <SelectBuscable
                  inputStyle={selectBuscableStyle}
                  ariaLabel="Puesto de trabajo"
                  value={positionId}
                  onChange={setPositionId}
                  disabled={jobPositions.length === 0}
                  vacio="— Seleccionar puesto —"
                  opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))}
                />
                {jobPositions.length === 0 && (
                  <span style={{ fontSize: '0.75rem', color: '#f59e0b', marginTop: 4, display: 'block' }}>
                    No tienes puestos creados. Ve a <strong>Puestos de Trabajo</strong> para crear uno.
                  </span>
                )}
              </Field>
              <Field label="Estado">
                <select style={selectStyle} value={status} onChange={e => setStatus(e.target.value as WorkerStatus)}>
                  {(Object.keys(STATUS_LABELS) as WorkerStatus[]).map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </Field>
            </Row>
            {conSede && (
              <Field label="Sede">
                <select style={selectStyle} value={siteId} onChange={e => setSiteId(e.target.value)} aria-label="Sede">
                  {opcionesSede(sedes, worker?.site_id).map(s => <option key={s.id} value={s.id}>{etiquetaSede(s)}</option>)}
                </select>
              </Field>
            )}
            <Field label="Bodega / Centro de trabajo">
              <SelectBuscable inputStyle={selectBuscableStyle} ariaLabel="Bodega / Centro de trabajo" value={warehouseId} onChange={setWarehouseId}
                vacio="— Sin asignar —" opciones={warehouses.map(w => ({ value: w.id, label: w.name }))} />
            </Field>
            <Row>
              <Field label="Tipo de contrato">
                <select style={selectStyle} value={contractType} onChange={e => setContractType(e.target.value as ContractType | '')}>
                  <option value="">— Seleccionar —</option>
                  {(Object.keys(CONTRACT_LABELS) as ContractType[]).map(ct => (
                    <option key={ct} value={ct}>{CONTRACT_LABELS[ct]}</option>
                  ))}
                </select>
              </Field>
              <Field label="Fecha de inicio">
                <input style={inputStyle} type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </Field>
            </Row>
            {contractType && contractType !== 'indefinido' && (
              <Field label="Fecha de fin de contrato">
                <input style={inputStyle} type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </Field>
            )}
          </Section>

          {/* ── Sección: SSO ──────────────────────────────────────────── */}
          <Section title="Seguridad y Salud (SSO)">
            <Field label="Nivel de riesgo del puesto">
              <div style={{ display: 'flex', gap: 8 }}>
                {(['bajo', 'medio', 'alto'] as RiskLevel[]).map(r => {
                  const rc = RISK_COLORS[r];
                  const active = riskLevel === r;
                  return (
                    <button key={r} onClick={() => setRiskLevel(active ? '' : r)} style={{
                      flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${active ? rc.border : '#334155'}`,
                      background: active ? rc.bg : 'transparent',
                      color: active ? rc.color : '#64748b',
                      fontWeight: active ? 700 : 500, fontSize: '0.82rem',
                      transition: 'all 0.15s', textTransform: 'capitalize',
                    }}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ToggleRow label="Teletrabajador" value={isTeleworker} onChange={setIsTeleworker} />
            </div>
            <AtencionPrioritariaSeccion companyId={companyId} workerId={worker?.id ?? null} birthDate={birthDate} esAdmin={profile?.role === 'admin'} />
            {/* M61 (TH 5): licencias de conducción (tipo y vencimiento; admin y manager las cambian, la base lo exige). */}
            <LicenciasConduccionSeccion companyId={companyId} workerId={worker?.id ?? null} positionId={worker?.position_id ?? null}
              puedeEditar={['admin', 'manager', 'superadmin'].includes(profile?.role ?? '')} usuarioId={profile?.id ?? null} />
          </Section>

          {/* ── Sección: Salud ocupacional ─────────────────────────────── */}
          <Section title="Salud ocupacional" badge="📋 Registra aquí el examen médico">
            <Row>
              <Field label="Fecha último examen">
                <input style={inputStyle} type="date" value={lastExamDate} onChange={e => setLastExamDate(e.target.value)} />
              </Field>
              <Field label="Tipo de examen">
                <select style={selectStyle} value={examType} onChange={e => setExamType(e.target.value as ExamType | '')}>
                  <option value="">— Seleccionar —</option>
                  {(Object.keys(EXAM_TYPE_LABELS) as ExamType[]).map(et => (
                    <option key={et} value={et}>{EXAM_TYPE_LABELS[et]}</option>
                  ))}
                </select>
              </Field>
            </Row>
            <Field label="Aptitud médica">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(Object.keys(APTITUDE_LABELS) as MedicalAptitude[]).map(a => {
                  const active = aptitude === a;
                  const color = a === 'apto' ? '#22c55e' : a === 'apto_con_restricciones' ? '#f59e0b' : a === 'no_apto' ? '#ef4444' : '#64748b';
                  return (
                    <button key={a} onClick={() => setAptitude(active ? '' : a)} style={{
                      padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '0.78rem',
                      border: `1px solid ${active ? color + '60' : '#334155'}`,
                      background: active ? color + '18' : 'transparent',
                      color: active ? color : '#64748b', fontWeight: active ? 700 : 500,
                      transition: 'all 0.15s',
                    }}>
                      {APTITUDE_LABELS[a]}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Médico responsable">
              <input style={inputStyle} value={physician} onChange={e => setPhysician(e.target.value)} placeholder="Nombre del médico" />
            </Field>
            <Field label="Observaciones">
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Restricciones, indicaciones, observaciones…" />
            </Field>

            {/* Historial — visible solo para admin/superadmin */}
            {canViewMedicalHistory && examHistory.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <p style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 8 }}>
                  HISTORIAL DE EXÁMENES
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {examHistory.map(ex => {
                    const aColor = ex.medical_aptitude === 'apto' ? '#22c55e' : ex.medical_aptitude === 'apto_con_restricciones' ? '#f59e0b' : ex.medical_aptitude === 'no_apto' ? '#ef4444' : '#64748b';
                    return (
                      <div key={ex.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        background: 'rgba(255,255,255,0.02)', border: '1px solid #1e293b',
                        borderRadius: 8, fontSize: '0.8rem',
                      }}>
                        <span style={{ color: '#94a3b8', minWidth: 84 }}>{new Date(ex.exam_date + 'T00:00:00').toLocaleDateString('es-EC')}</span>
                        <span style={{ color: '#cbd5e1', flex: 1 }}>{ex.exam_type ? EXAM_TYPE_LABELS[ex.exam_type] : '—'}</span>
                        <span style={{ color: aColor, fontWeight: 600 }}>{APTITUDE_LABELS[ex.medical_aptitude]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Section>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isEdit && worker?.status !== 'desvinculado' && !confirmDesvincular && !confirmDelete && (
              <button onClick={() => setConfirmDesvincular(true)} style={{
                padding: '9px 16px', background: 'transparent', border: '1px solid rgba(239,68,68,0.4)',
                color: '#f87171', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
              }}>
                Desvincular
              </button>
            )}
            {confirmDesvincular && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#f87171', fontSize: '0.8rem' }}>¿Confirmar desvinculación?</span>
                <button onClick={handleDesvincular} disabled={saving} style={{
                  padding: '6px 12px', background: '#ef4444', border: 'none', color: '#fff',
                  borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
                }}>
                  {saving ? '…' : 'Confirmar'}
                </button>
                <button onClick={() => setConfirmDesvincular(false)} style={{
                  padding: '6px 10px', background: 'transparent', border: '1px solid #334155',
                  color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem',
                }}>
                  Cancelar
                </button>
              </div>
            )}
            {isEdit && !confirmDelete && !confirmDesvincular && (
              <button onClick={() => setConfirmDelete(true)} style={{
                background: 'transparent', border: '1px solid #dc2626', color: '#dc2626',
                padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem',
              }}>
                Eliminar definitivamente
              </button>
            )}
            {confirmDelete && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ color: '#dc2626', fontSize: '0.8rem', fontWeight: 600 }}>
                  ⚠️ Esto eliminará permanentemente al trabajador y su historial médico. No se puede deshacer.
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleDelete} disabled={saving} style={{
                    background: '#dc2626', color: 'white', border: 'none',
                    padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
                  }}>
                    {saving ? '…' : 'Sí, eliminar definitivamente'}
                  </button>
                  <button onClick={() => setConfirmDelete(false)} style={{
                    background: 'transparent', border: '1px solid #6b7280', color: '#6b7280',
                    padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
                  }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{
              padding: '9px 20px', background: 'transparent', border: '1px solid #334155',
              color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600,
            }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
              border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer',
              fontSize: '0.88rem', fontWeight: 700, opacity: saving ? 0.7 : 1,
            }}>
              {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Agregar trabajador'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Section({ title, children, badge }: { title: string; children: React.ReactNode; badge?: string }) {
  return (
    <div style={{ marginTop: 16 }}>
      <p style={{
        color: badge ? '#60a5fa' : '#64748b', fontSize: '0.72rem', fontWeight: 700,
        letterSpacing: '0.06em', marginBottom: 12, textTransform: 'uppercase',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {title}
        {badge && (
          <span style={{
            color: '#60a5fa', background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)',
            borderRadius: 5, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700,
            textTransform: 'none', letterSpacing: 'normal',
          }}>
            {badge}
          </span>
        )}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
      {error && <span style={{ color: '#f87171', fontSize: '0.74rem' }}>{error}</span>}
    </div>
  );
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => onChange(!value)}>
      <div style={{
        width: 38, height: 20, borderRadius: 10, flexShrink: 0, position: 'relative',
        background: value ? 'rgba(47,191,113,0.25)' : '#334155',
        border: `2px solid ${value ? '#2FBF71' : '#475569'}`, transition: 'all 0.2s',
      }}>
        <div style={{
          width: 12, height: 12, borderRadius: '50%', position: 'absolute',
          top: 2, left: value ? 20 : 2, transition: 'left 0.2s',
          background: value ? '#2FBF71' : '#64748b',
        }} />
      </div>
      <span style={{ color: '#cbd5e1', fontSize: '0.84rem' }}>{label}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)',
  border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0',
  fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle, cursor: 'pointer',
};
// PE-C1: puesto y bodega con buscador (mismo aspecto; espacio a la derecha para la flecha).
const selectBuscableStyle: React.CSSProperties = { ...inputStyle, paddingRight: 32 };
